import {
  copilotChatHeaderButtonID,
  copilotChatPanelID,
  copilotChatPanelInnerID,
} from '@github-ui/copilot-chat/utils/constants'
import {COPILOT_PATH, isRepository} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {ResizeEdgeHorizontal, ResizeEdgeVertical} from '@github-ui/copilot-chat/utils/copilot-chat-hooks'
import {MIN_PANEL_HEIGHT, MIN_PANEL_WIDTH} from '@github-ui/copilot-chat/utils/copilot-chat-hooks'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {useNavigate} from '@github-ui/use-navigate'
import {useIsSafari} from '@github-ui/use-sticky-header/useIsSafari'
import {Overlay} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useCallback, useEffect, useRef} from 'react'

import styles from './ChatPanel.module.css'

interface ChatPanelProps {
  handleClose: (usedEscapeKey?: boolean) => void
  staffDialogRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
  panelWidth: number
  panelHeight: number
  startResize: (e: React.MouseEvent, horizontal: ResizeEdgeHorizontal, vertical: ResizeEdgeVertical) => void
  onResizerKeyDown: (e: React.KeyboardEvent) => void
  initialFocusRef: React.RefObject<HTMLTextAreaElement | null>
  positionRight?: number
  positionBottom?: number
  isDragging?: boolean
}

// The height of the header and resizers.
const HEADER_HEIGHT = 60

export const ChatPanel = (props: ChatPanelProps) => {
  const {
    children,
    initialFocusRef,
    staffDialogRef,
    handleClose,
    panelWidth,
    panelHeight,
    startResize,
    onResizerKeyDown,
    positionRight = 8,
    positionBottom = 8,
    isDragging = false,
  } = props
  const state = useChatStateValues(
    'chatIsOpen',
    'currentReferences',
    'currentTopic',
    'entryPointId',
    'selectedThreadID',
    'threads',
  )
  const {chatIsOpen, currentTopic} = state
  const entryPointId = state.entryPointId ?? copilotChatHeaderButtonID
  const overlayRef = useRef<HTMLDivElement>(null)
  const entryPointRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const entryElement = document.getElementById(entryPointId)
    entryPointRef.current = entryElement
  }, [entryPointId])

  const onEscape = useCallback(() => {
    if (overlayRef.current && overlayRef.current.contains(document.activeElement)) {
      handleClose(true)
    }
  }, [handleClose])

  const manager = useChatManager()
  const thread = getSelectedThread(state)
  const navigate = useNavigate()
  const isSafari = useIsSafari()

  useEffect(() => {
    if (chatIsOpen) {
      const result = manager.consumePanelOpenStartTime()
      if (result != null) {
        const duration = performance.now() - result.startTime
        sendEvent('dotcom_chat.panel_open_duration', {
          duration_ms: Math.round(duration),
          is_first_open: result.isFirstOpen,
        })
      }
    }
  }, [chatIsOpen, manager])
  const navigateToImmersive = () => {
    sendEvent('dotcom_chat.activate', {target: 'IMMERSIVE_OPTION', mode: 'assistive'})

    // References are saved automatically whenever they change, but it's possible that another tab might have overwritten
    // the state since this tab was opened. Since the user is clicking in this tab, they expect these to be the references
    // that carry over into immersive.
    copilotLocalStorage.setCurrentReferences(thread?.id ?? null, state.currentReferences)

    // Save the current input text immediately before navigating. The regular save is debounced,
    // so we need to use the fast (non-debounced) version to ensure the text is saved before navigation.
    // We save to 'immersive' source since that's where the user is navigating to.
    const currentText = initialFocusRef.current?.value ?? ''
    copilotLocalStorage.setSavedMessageFast(thread?.id ?? null, 'immersive', currentText)

    if (thread) {
      navigate(`${COPILOT_PATH}/c/${thread.id}`)
    } else if (currentTopic && isRepository(currentTopic)) {
      navigate(`${COPILOT_PATH}/r/${currentTopic.ownerLogin}/${currentTopic.name}`)
    } else {
      navigate(COPILOT_PATH)
    }
  }

  return (
    <>
      {chatIsOpen ? (
        <Overlay
          id={copilotChatPanelID}
          className={clsx(styles.copilotChatPanel, styles.openChat, isDragging && styles.panelDragging)}
          ref={overlayRef}
          onEscape={onEscape}
          onClickOutside={() => {}}
          ignoreClickRefs={[staffDialogRef]}
          initialFocusRef={initialFocusRef}
          returnFocusRef={entryPointRef}
          style={{
            '--panel-width': `${panelWidth}px`,
            '--panel-height': `${panelHeight}px`,
            '--panel-min-height': `${MIN_PANEL_HEIGHT + HEADER_HEIGHT}px`,
            '--panel-min-width': `${MIN_PANEL_WIDTH}px`,
          }}
          right={positionRight}
          bottom={positionBottom}
          anchorSide="inside-top"
          position="fixed"
          role="dialog"
          aria-labelledby={copilotChatPanelInnerID}
        >
          <GlobalCommands
            commands={{
              // This should be runnable from anywhere on the page so you can close the chat without tabbing to it
              'copilot-chat:close-assistive': () => manager.closeChat('keyboard-shortcut'),
            }}
          />
          <ScopedCommands
            commands={{
              // Scoped to the chat panel so that, while focus is inside the assistive chat, the unified `Shift+C`
              // immersive shortcut continues the current conversation here (taking precedence over the global
              // `open-immersive`, which opens a fresh immersive session everywhere else).
              'copilot-chat:continue-in-immersive': navigateToImmersive,
            }}
          >
            <section id={copilotChatPanelInnerID} aria-label="Copilot chat panel" style={{height: '100%'}}>
              <div data-testid={copilotChatPanelInnerID} className={styles.ChatPanelContainer}>
                {/* Edge resize targets */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, false, 'top')}
                  className={clsx(styles.verticalResizeTarget, isSafari && styles.safariVerticalResize)}
                />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, false, 'bottom')}
                  className={clsx(styles.verticalResizeTargetBottom, isSafari && styles.safariVerticalResize)}
                />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, 'left', false)}
                  className={clsx(styles.horizontalResizeTarget, isSafari && styles.safariHorizontalResize)}
                />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, 'right', false)}
                  className={clsx(styles.horizontalResizeTargetRight, isSafari && styles.safariHorizontalResize)}
                />

                {/* Corner resize targets */}
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                <div
                  aria-label="Chat panel resizer"
                  aria-valuetext={`${panelWidth}, ${panelHeight}`}
                  aria-valuenow={panelWidth}
                  role="separator"
                  onMouseDown={e => startResize(e, 'left', 'top')}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  onKeyDown={onResizerKeyDown}
                  className={styles.cornerResizeTarget}
                />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div onMouseDown={e => startResize(e, 'right', 'top')} className={styles.cornerResizeTargetTopRight} />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, 'left', 'bottom')}
                  className={styles.cornerResizeTargetBottomLeft}
                />
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onMouseDown={e => startResize(e, 'right', 'bottom')}
                  className={styles.cornerResizeTargetBottomRight}
                />

                <div className={styles.panelContentContainer}>{children}</div>
              </div>
            </section>
          </ScopedCommands>
        </Overlay>
      ) : null}
    </>
  )
}
